import { DocumentReference } from "fhir/r5";
import dayjs from "dayjs";

/**
 * Utilitários para processamento de documentos FHIR
 * Centraliza lógica comum de filtragem, extração e transformação de dados
 */

/**
 * Verifica se um documento está assinado (tem ProofOfOrigin)
 */
export function isDocumentSigned(resource: any): boolean {
  return (
    resource?.securityLabel?.[0]?.coding?.[0]?.code === "ProofOfOrigin"
  );
}

/**
 * Filtra documentos baseado no status de assinatura
 * @param entries - Array de entradas FHIR
 * @param sign - Se true, retorna apenas documentos assinados; se false, apenas não assinados
 */
export function filterBySignStatus(
  entries: any[],
  sign: boolean,
): any[] {
  if (!entries) return [];

  return entries.filter((e: any) => {
    const isSigned = isDocumentSigned(e?.resource);
    return sign ? isSigned : !isSigned && e?.resource?.resourceType === "DocumentReference";
  });
}

/**
 * Extrai informações do practitioner de um recurso contained
 */
export function extractPractitionerInfo(resource: any): {
  name: string;
  identifier: string;
} {
  const practitioner = resource?.contained?.find(
    (contained: any) => contained?.resourceType === "Practitioner",
  );

  return {
    name: practitioner?.name?.[0]?.given?.[0] || "",
    identifier: practitioner?.identifier?.[0]?.value || "",
  };
}

/**
 * Extrai a especialidade do practiceSetting
 */
export function extractSpecialty(resource: any): string {
  return resource?.practiceSetting?.coding?.[0]?.display || "-";
}

/**
 * Extrai a URL do anexo binário do documento
 */
export function extractBinaryUrl(resource: any): string {
  return resource?.content?.[0]?.attachment?.url || "";
}

/**
 * Formata data usando dayjs
 */
export function formatDocumentDate(date: string | undefined): string {
  return date ? dayjs(date).format("DD/MM/YYYY") : "-";
}

/**
 * Limpa e junta expressões, removendo duplicatas e valores vazios
 */
export function cleanAndJoin(
  expressions: any[],
  delimiter: string = ", ",
): string {
  return Array.from(
    new Set(expressions.filter((item: any) => item && item.trim() !== "")),
  ).join(delimiter);
}

/**
 * Traduz categorias de inglês para português
 */
export function translateCategory(category: string): string {
  const translations: Record<string, string> = {
    "Laboratory procedure": "Exame Laboratorial",
    Imaging: "Exame de imagem",
  };

  return translations[category] || category;
}

/**
 * Busca recursos relacionados baseado em referências basedOn
 */
export function findRelatedResources(
  documentResource: any,
  relatedResources: any[],
): any[] {
  if (!relatedResources || !documentResource?.basedOn) return [];

  return relatedResources.filter((request: any) => {
    return documentResource.basedOn?.find((basedOn: any) =>
      basedOn?.reference?.includes(request.resource.id),
    );
  });
}

/**
 * Extrai URLs de resultados de recursos relacionados
 */
export function extractResultUrls(results: any[]): string[] {
  if (!results) return [];

  return results
    .map(
      (result: any) => result?.resource?.content?.[0]?.attachment?.url || "",
    )
    .filter((url: string) => url !== "");
}

/**
 * Processa dados básicos de um documento para exibição
 */
export function processDocumentBaseData(
  entry: fhir5.BundleEntry<DocumentReference | any>,
  sign: boolean,
): {
  fullResource: any;
  practitioner: string;
  date: string;
  specialty: string;
  sign: boolean;
  binaryUrl: string;
} {
  const { name, identifier } = extractPractitionerInfo(entry?.resource);

  return {
    fullResource: entry?.resource,
    practitioner: name || identifier,
    date: formatDocumentDate(entry?.resource?.date),
    specialty: extractSpecialty(entry?.resource),
    sign,
    binaryUrl: extractBinaryUrl(entry?.resource),
  };
}
